import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import RolesUtil from 'permissions/utils/roles';
import { ReactComponent as Information } from 'icons/info.svg';
import { ReactComponent as ErrorIcon } from 'icons/error.svg';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import { ReactComponent as CloseButton } from 'icons/close-button.svg';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  AUTO_TIEOUT_COMPARE_MODE_NEW,
  AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
  AUTO_TIEOUT_COMPARE_MODE_SUCCESS,
  AUTO_TIEOUT_COMPARE_MODE_FAILED,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import { resetAutoTieoutStatusForBanner } from 'store/actions/auto-tieout/workpaper-details-action';
import { AUTO_TIEOUT_BANNER_MESSAGES } from 'constants/util/side-by-side-utils';

const CLOSE_ICON_DIMENSION = '28px';

export const SideBySideViewBanner = ({
  BLOCK,
  stateData,
  projectId,
  showAutoTieoutInfoBanner,
  leftRevision,
  leftSelectedStatement,
  sideBySideViewMode,
  workFlowStatus,
  handleRetryAutoTieout,
  setShowAutoTieoutInfoBanner,
  isSnapshotReadOnly,
  selectedStatement,
}) => {
  const isStale = workFlowStatus?.stale;
  const dispatch = useDispatch();
  const isAutoTieoutMode =
    sideBySideViewMode?.viewMode === AUTO_TIEOUT_COMPARE_MODE;
  const isTrialBalanceMode =
    sideBySideViewMode?.viewMode === TRAIL_BALANCE_MODE;
  const autoTieoutStatus = sideBySideViewMode?.getAutoTieoutStatusForBanner();
  const showRetry = sideBySideViewMode?.showRetry;

  const isInProgress =
    autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_NEW ||
    autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_INPROGRESS;
  const isSuccess = autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_SUCCESS;
  const isFailed = autoTieoutStatus === AUTO_TIEOUT_COMPARE_MODE_FAILED;

  const isTargetStatementWrappedUpOrPendingApproval =
    selectedStatement?.isStatementWrappedOrPendingApproval();

  const shouldShowBannerByRoleAndStatementStatus = () => {
    const hasObserverRole = RolesUtil.doesUserHaveObserverRoleForProject(
      stateData,
      projectId,
    );

    if (isAutoTieoutMode || isTrialBalanceMode) {
      return (
        isTargetStatementWrappedUpOrPendingApproval ||
        hasObserverRole ||
        (isTrialBalanceMode && isSnapshotReadOnly)
      );
    }

    return false;
  };
  const shouldShowAutoTieoutBanner = () => {
    if (
      shouldShowBannerByRoleAndStatementStatus() ||
      !showAutoTieoutInfoBanner
    ) {
      return false;
    }

    const isAutoTieoutConditionMet =
      leftRevision.isLoaded &&
      leftSelectedStatement.isLoaded &&
      isAutoTieoutMode;

    return (isAutoTieoutConditionMet || isTrialBalanceMode) && autoTieoutStatus;
  };

  const isRetryDisabled =
    (isTrialBalanceMode && isSnapshotReadOnly) ||
    RolesUtil.doesUserHaveObserverRoleForProject(stateData, projectId);

  const getAutoTieoutBannerMessage = () => {
    if (isInProgress) {
      return AUTO_TIEOUT_BANNER_MESSAGES.INPROGRESS;
    }

    if (isSuccess) {
      return AUTO_TIEOUT_BANNER_MESSAGES.SUCCESS;
    }

    if (isFailed) {
      return workFlowStatus?.isRetryable
        ? isTrialBalanceMode
          ? AUTO_TIEOUT_BANNER_MESSAGES.FAILURE_TRIAL_BALANCE
          : AUTO_TIEOUT_BANNER_MESSAGES.FAILURE_AUTO_TIEOUT_TO_ANOTHER_STATEMENT
        : AUTO_TIEOUT_BANNER_MESSAGES.MAX_RETRIES;
    }

    return null;
  };

  const bannerMessage = getAutoTieoutBannerMessage();

  return (
    <>
      {isStale && isTrialBalanceMode && (
        <div
          className={classnames(
            `${BLOCK}__auto-tieout-banner`,
            `${BLOCK}__auto-tieout-banner--alert`,
          )}
        >
          <div className={`${BLOCK}__auto-tieout-banner-header`}>
            <div className={`${BLOCK}__auto-tieout-banner-message`}>
              <FormattedMessage id="trial-balance-view.omnia.tb.update.alert" />
            </div>
          </div>
        </div>
      )}
      {shouldShowAutoTieoutBanner() && bannerMessage && (
        <div
          className={classnames(`${BLOCK}__auto-tieout-banner`, {
            [`${BLOCK}__auto-tieout-banner--in-progress`]: isInProgress,
            [`${BLOCK}__auto-tieout-banner--error`]: isFailed,
          })}
        >
          <div className={`${BLOCK}__auto-tieout-banner-header`}>
            {isFailed ? (
              <ErrorIcon className={`${BLOCK}__auto-tieout-banner-icon`} />
            ) : (
              <Information className={`${BLOCK}__auto-tieout-banner-icon`} />
            )}
            <div className={`${BLOCK}__auto-tieout-banner-message`}>
              <FormattedMessage
                id={getAutoTieoutBannerMessage()}
                values={{ maxRetries: workFlowStatus?.maxRetries ?? 0 }}
              />
            </div>
            {isFailed && workFlowStatus?.isRetryable && showRetry && (
              <Button
                type={BUTTON_TYPES.tertiary}
                onClick={() => {
                  if (!isRetryDisabled) {
                    handleRetryAutoTieout();
                  }
                }}
                className={`${BLOCK}__auto-tieout-retry-button`}
                disabled={isRetryDisabled}
              >
                <FormattedMessage id="side-by-side-view.compare.auto-tieout-retry.button" />
              </Button>
            )}
            <CloseButton
              className={`${BLOCK}__close`}
              onClick={() => {
                setShowAutoTieoutInfoBanner(false);
                dispatch(resetAutoTieoutStatusForBanner());
              }}
              height={CLOSE_ICON_DIMENSION}
              width={CLOSE_ICON_DIMENSION}
              role="button"
            />
          </div>
        </div>
      )}
    </>
  );
};

SideBySideViewBanner.propTypes = {
  /** Block name for BEM class naming */
  BLOCK: PropTypes.string.isRequired,
  /** onClick handler for swap icon */
  onClick: PropTypes.func.isRequired,
};

export default SideBySideViewBanner;
